import { type JSX, useEffect, useRef, useState } from 'react';

import type { MessageKeys } from '../i18n/messages/index.js';
import { useLanguage } from '../i18n/useLanguage.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { Button } from '../primitives/Button.js';
import {
  MyDataCategoryCard,
  MyDataEmptyNote,
  MyDataField,
} from '../routes/profile/MyDataCategoryCard.js';
import type {
  MyDataSuccessfulMemberMessage,
  MyDataSuccessfulMemberMessagesPage,
  MyDataSuccessfulMemberMessagesPageReader,
} from './MyDataPort.js';

export interface SuccessfulMemberMessagesDataSectionProps {
  /** The first successful-message page supplied by `member.myData.view`. */
  readonly successfulMemberMessages: MyDataSuccessfulMemberMessagesPage;
  /** The same request-scoped adapter, used only for this collection's next page. */
  readonly reader: MyDataSuccessfulMemberMessagesPageReader;
}

type PagingPhase = 'idle' | 'loading' | 'error';

interface PagingState {
  readonly successfulMemberMessages: MyDataSuccessfulMemberMessagesPage;
  readonly reader: MyDataSuccessfulMemberMessagesPageReader;
  readonly page: MyDataSuccessfulMemberMessagesPage;
  readonly phase: PagingPhase;
}

const SERVICE_CATEGORY_KEYS = {
  lakikaveri: 'myData.successfulMemberMessages.category.lakikaveri',
  jasenasiat: 'myData.successfulMemberMessages.category.jasenasiat',
} as const satisfies Record<MyDataSuccessfulMemberMessage['serviceCategory'], MessageKeys>;

const MESSAGE_KIND_KEYS = {
  initial: 'myData.successfulMemberMessages.kind.initial',
  reply: 'myData.successfulMemberMessages.kind.reply',
} as const satisfies Record<MyDataSuccessfulMemberMessage['messageKind'], MessageKeys>;

function formatInstant(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const resolvedLocale = locale === 'sv' ? 'sv-SE' : locale === 'en' ? 'en-GB' : 'fi-FI';
  return new Intl.DateTimeFormat(resolvedLocale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function factKey(fact: MyDataSuccessfulMemberMessage, pageIndex: number): string {
  // The API deliberately withholds fact IDs. Pages replace their immutable
  // rows as a unit, and rows have no local state, so the page-local index
  // disambiguates otherwise identical safe projections without exposing IDs.
  return `${fact.occurredAt}|${fact.serviceCategory}|${fact.messageKind}|${pageIndex}`;
}

function SuccessfulMemberMessageFact({
  fact,
}: {
  readonly fact: MyDataSuccessfulMemberMessage;
}): JSX.Element {
  const t = useTranslate();
  const { active: locale } = useLanguage();
  return (
    <article className="border-b border-border last:border-b-0">
      <dl className="flex flex-col">
        <MyDataField
          label={t('myData.successfulMemberMessages.occurredAt')}
          value={formatInstant(fact.occurredAt, locale)}
        />
        <MyDataField
          label={t('myData.successfulMemberMessages.serviceCategory')}
          value={t(SERVICE_CATEGORY_KEYS[fact.serviceCategory])}
        />
        <MyDataField
          label={t('myData.successfulMemberMessages.messageKind')}
          value={t(MESSAGE_KIND_KEYS[fact.messageKind])}
        />
      </dl>
    </article>
  );
}

export function SuccessfulMemberMessagesDataSection({
  successfulMemberMessages,
  reader,
}: SuccessfulMemberMessagesDataSectionProps): JSX.Element {
  const t = useTranslate();
  const [state, setState] = useState<PagingState>({
    successfulMemberMessages,
    reader,
    page: successfulMemberMessages,
    phase: 'idle',
  });
  const requestId = useRef(0);
  const activeScope = useRef({ successfulMemberMessages, reader });

  // A new account receives its first page through `view`. Reset during render
  // so prior account facts never commit onscreen while the replacement renders.
  if (state.successfulMemberMessages !== successfulMemberMessages || state.reader !== reader) {
    setState({
      successfulMemberMessages,
      reader,
      page: successfulMemberMessages,
      phase: 'idle',
    });
  }

  useEffect(() => {
    activeScope.current = { successfulMemberMessages, reader };
    return () => {
      requestId.current += 1;
    };
  }, [successfulMemberMessages, reader]);

  const current =
    state.successfulMemberMessages === successfulMemberMessages && state.reader === reader
      ? state
      : {
          ...state,
          page: successfulMemberMessages,
          phase: 'idle' as const,
        };
  const page = current.page;

  async function loadNextPage(): Promise<void> {
    if (page.status !== 'available' || page.nextCursor === null || current.phase === 'loading') {
      return;
    }

    const request = requestId.current + 1;
    requestId.current = request;
    setState({ ...current, phase: 'loading' });
    try {
      const nextPage = await reader.getSuccessfulMemberMessagesPage(page.nextCursor);
      if (
        request === requestId.current &&
        activeScope.current.successfulMemberMessages === successfulMemberMessages &&
        activeScope.current.reader === reader
      ) {
        setState({ successfulMemberMessages, reader, page: nextPage, phase: 'idle' });
      }
    } catch {
      if (
        request === requestId.current &&
        activeScope.current.successfulMemberMessages === successfulMemberMessages &&
        activeScope.current.reader === reader
      ) {
        setState({ ...current, phase: 'error' });
      }
    }
  }

  return (
    <MyDataCategoryCard
      heading={t('myData.successfulMemberMessages.heading')}
      ownership={t('myData.ownership.app')}
    >
      {page.status === 'unavailable' ? (
        <MyDataEmptyNote>{t('myData.successfulMemberMessages.unavailable')}</MyDataEmptyNote>
      ) : page.facts.length === 0 ? (
        <MyDataEmptyNote>{t('myData.successfulMemberMessages.empty')}</MyDataEmptyNote>
      ) : (
        <div className="flex flex-col">
          {page.facts.map((fact, pageIndex) => (
            <SuccessfulMemberMessageFact key={factKey(fact, pageIndex)} fact={fact} />
          ))}
        </div>
      )}

      {page.status === 'available' && page.nextCursor !== null && (
        <div className="flex flex-wrap items-center gap-3 border-t border-border px-4 py-3">
          {current.phase === 'error' && (
            <p role="alert" className="text-sm text-destructive">
              {t('myData.successfulMemberMessages.pageError')}
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={current.phase === 'loading'}
            onClick={() => {
              void loadNextPage();
            }}
          >
            {current.phase === 'loading'
              ? t('myData.successfulMemberMessages.loadingNext')
              : current.phase === 'error'
                ? t('myData.successfulMemberMessages.retry')
                : t('myData.successfulMemberMessages.nextPage')}
          </Button>
        </div>
      )}
    </MyDataCategoryCard>
  );
}
